import {userService} from '../_services';
import {router, setAxiosDefaultHeaders} from '../_helpers';
import app from "@/main";

/**
 * This dictionary represents the User's information which may be stored on the Local Storage.
 * The value for each field may be defined using specific mutations.
 * These User's attributes are checked in several moments of the application.
 * For example: localStorage.getItem('user') or this.$store.state.authentication.user.username
 * @type {{firstName: null, lastName: null, mandatoryChangePassword: null, username: null, token: null, mandatoryValidateProfile: null}}
 */
const user_data = {
    "username": null,
    "firstName": null,
    "lastName": null,
    "access": null,  // @modified ana.castro 2019.09.24 SCLAPP-20
    "refresh": null, // @modified ana.castro 2019.09.24 SCLAPP-20
    // "token": null, // @modified ana.castro 2019.09.24 SCLAPP-20
    "mandatoryValidateProfile": null,
    "mandatoryChangePassword": null
};

/**
 * If the authentication method it's through a social provider (FB, IG or Google) the stored information
 * consists on identifying the Provider and the given AccessToken/Code which identifies the user.
 * @type {{code: null, provider: null}}
 */
const social_provider = {
        'provider': null,
        'code': null
    }
;

const user = JSON.parse(localStorage.getItem('user'));
const initialState = user
    ? {status: {loggedIn: true}, user, login_provider: social_provider}
    : {status: {}, user: user_data, login_provider: social_provider};

export const authentication = {
    namespaced: true,
    state: initialState,
    actions: {
        setLoginUsername({commit}, username) {
            commit('setLoginUsername', username);
        },
        setLoginProvider({commit}, {provider, code}) {
            commit('setLoginProvider', {provider, code});
        },
        /**
         * This function is called when a success answer is received from the backend, indicating the Login's process
         * had the expected behaviour.
         * The success answer has a flag which can have two different values and depending on this value, the following steps may differ:
         * 1 - It is a New User (result.info_user === "newUser"):
         * the user is redirected to the Profile interface where he can fill in his personal data;
         * the profile can be filled in with some values depending on what the Login Social Provider shared with us.
         * (this applies to a login process in a context of using a Social Login Provider)
         * 2 - It's an Existing User (result.info_user === "existingUser" or None):
         * sets the Store loggedIn status to True; sets the flags for mandatory changing password and
         * mandatory accepting RGPD according to the data received from backend;
         * the user is redirected to the Home interface where he can fill in his personal data
         */
        setInfoAfterLogin({dispatch, commit}, {result}) {
            app.$log.debug("Backend successful answer to the Login process");
            app.$log.debug("Login Process Received Data:");
            app.$log.debug(JSON.stringify(result));
            //1 - It is a New User (result.info_user === "newUser")
            if (result.info_user === "newUser") {
                app.$log.debug("It's a New User");
                // The user is redirected to the Profile interface where he can fill in his personal data
                router.push({
                    name: "user-register",
                    // the profile can be filled in with some values depending on what the Login Social Provider shared with us.
                    // (this applies to a login process in a context of using a Social Login Provider)
                    params: {
                        name: result.user_info.name,
                        email_address: result.user_info.email,
                        gender: result.user_info.gender,
                        birth_date: result.user_info.birthday
                    }
                });
            }
            //2 - It's an Existing User (result.info_user === "existingUser" or None)
            else {
                // sets the Store loggedIn status to True; sets the flags for mandatory changing password and
                // mandatory accepting RGPD according to the data received from backend;
                commit('loginSuccess', result);

                // On Login must set the Authentication header
                setAxiosDefaultHeaders();

                // The user is redirected to the Home interface where he can fill in his personal data
                if (result.mandatoryValidateProfile === 1){
                    router.push({name: "user"});
                } else {
                    router.push({name: "root"});
                }
            }
            // Removing the Loading Layer
            dispatch("loader/setLoading", false, {root: true});
        },
        /**
         * The Login Function which uses the correct User Service depending on the login's type (password or social provider).
         * After calling the correct User Service and receiving the Server's answer,
         *  it analyses the response and sets a few flags according to it
         * @param dispatch
         * @param commit
         * @param method: password or oauth
         * @param params: {username, password and recaptcha token} OR {social provider access token}
         */
        loginGeneric({dispatch, commit}, {method, params}) {
            //commit('loginRequest', {username});
            app.$log.debug("Login Generic | Method: " + method + " | Parameters: " + JSON.stringify(params));

            //Identifying the Login method based on the received parameter
            let loginMethod = undefined;
            if (method === 'password') {
                let {username, password, token} = params;
                loginMethod = userService.login(username, password, token);
            } else {
                let {provider, code} = params;
                loginMethod = userService.loginSocialProvider(provider, code);
            }

            //Calling the User Service login method
            loginMethod
                .then(
                    result => {
                        if (result.status === 1) {
                            dispatch('setInfoAfterLogin', {result});
                        } else {
                            // Removing the Loading Layer
                            dispatch("loader/setLoading", false, {root: true});
                            commit('loginFailure', result.error_description);
                            dispatch('alert/error', '$vuetify.authentication.login_error', {root: true});
                        }
                    },
                    error => {
                        // Removing the Loading Layer
                        dispatch("loader/setLoading", false, {root: true});
                        commit('loginFailure', error);
                        dispatch('alert/error', '$vuetify.authentication.login_error', {root: true});
                    }
                );
        },
        /**
         * Login Function when the login method is password. This means the user indicates its username and password.
         * @param dispatch
         * @param commit
         * @param username
         * @param password
         * @param token
         */
        // eslint-disable-next-line
        login({dispatch, commit}, {username, password, token}) {
            let method = 'password';
            let params = {username, password, token};
            dispatch('loginGeneric', {method, params});
        },
        /**
         * Login Function when the login method uses a Social Provider like Facebook or Instagram
         * @param dispatch
         * @param commit
         * @param provider: Facebook (FB) or Instagram (IG)
         * @param code
         */
        // eslint-disable-next-line
        loginSocialProvider({dispatch, commit}, {provider, code}) {
            // commit('loginRequest', {username});
            let method = 'oauth';
            let params = {provider, code};
            dispatch('loginGeneric', {method, params});
        },
        logout({commit}) {
            userService.logout();
            // On LogOut must set the Authentication header to NULL
            setAxiosDefaultHeaders();
            commit('logout');
        },
        refreshToken({dispatch, commit}) {
            // @modified ana.castro 2019.09.24 SCLAPP-20 It is mandatory to return a Promise
            return new Promise((resolve, reject) => {
                userService.refreshToken().then(token => {
                    if (token) {
                        commit('refreshSuccess');
                        setAxiosDefaultHeaders();
                        resolve(token);
                    } else {
                        commit('logout');
                        // The user is redirected to the login interface
                        router.push({name: "login"});
                        // Removing the Loading Layer
                        dispatch("loader/setLoading", false, {root: true});
                    }
                }, error => {
                    app.$log.warn(error);
                    commit('logout');
                    reject(error);
                    // The user is redirected to the login interface
                    router.push({name: "login"});
                    // Removing the Loading Layer
                    dispatch("loader/setLoading", false, {root: true});
                });
            });
        },
        setValidateProfile({commit}, validateProfile) {
            commit('setValidateProfile', validateProfile);
        },
    },
    mutations: {
        setLoginUsername(state, username) {
            state.status = {loggingIn: false};
            state.user.username = username;
        },
        setLoginProvider(state, {provider, code}) {
            state.status = {loggingIn: false};
            state.user = user_data;
            state.login_provider = {'provider': provider, 'code': code};
        },
        // loginRequest(state, user) {
        //     state.status = {loggingIn: true};
        //     state.user = user;
        // },
        loginSuccess(state, user_data) {
            state.status = {loggedIn: true};
            state.user.username = user_data.username;
            state.user.firstName = user_data.firstName;
            state.user.lastName = user_data.lastName;
            // @modified ana.castro 2019.09.24 SCLAPP-20
            // state.user.token = user_data.token;
            state.user.access = user_data.access;
            state.user.refresh = user_data.refresh;
            state.user.mandatoryValidateProfile = user_data.mandatory_validate_profile;
            state.user.mandatoryChangePassword = user_data.mandatory_change_password;
            localStorage.setItem('user', JSON.stringify(state.user));
        },
        loginFailure(state) {
            state.status = {};
            state.user = user_data;
        },
        logout(state) {
            state.status = {};
            state.user = {
                "username": null,
                "firstName": null,
                "lastName": null,
                "access": null,
                "refresh": null,
                "mandatoryValidateProfile": null,
                "mandatoryChangePassword": null
            }
            state.login_provider = {
                'provider': null,
                'code': null
            };
        },
        refreshSuccess() {
            app.$log.debug("refreshSuccess");
        },
        setValidateProfile(state, validateProfile) {
            state.user.mandatoryValidateProfile = validateProfile;
            localStorage.setItem('user', JSON.stringify(user));
        },
    }
};
